import React from 'react';

export interface CustomerRiskSummary {
  status: 'ok' | 'no_history' | 'unavailable' | 'rate_limited' | 'not_configured' | string;
  total?: number;
  delivered?: number;
  cancelled?: number;
  delivery_rate?: number;
  return_rate?: number;
  risk_level?: 'trusted' | 'high_risk' | 'moderate' | 'new' | string;
}

interface CustomerRiskBadgeProps {
  data?: CustomerRiskSummary | null;
  loading?: boolean;
  onClick?: () => void;
}

export const CustomerRiskBadge: React.FC<CustomerRiskBadgeProps> = ({ data, loading, onClick }) => {
  if (loading) {
    return (
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          padding: '1px 6px',
          borderRadius: '9999px',
          fontSize: '10px',
          fontWeight: 600,
          background: '#f8fafc',
          color: '#94a3b8',
          border: '1px solid #e2e8f0',
          marginTop: '3px',
          userSelect: 'none',
        }}
        title="Checking courier delivery intelligence..."
      >
        <span
          style={{
            display: 'inline-block',
            width: '5px',
            height: '5px',
            borderRadius: '50%',
            backgroundColor: '#94a3b8',
            opacity: 0.7,
          }}
        />
        <span>Checking...</span>
      </div>
    );
  }

  if (!data) return null;

  const { status, total = 0, delivered = 0, cancelled = 0, delivery_rate = 0, return_rate = 0, risk_level } = data;

  if (status === 'no_history' || risk_level === 'new' || total === 0) {
    return (
      <div
        onClick={onClick}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '3px',
          padding: '1px 7px',
          borderRadius: '9999px',
          fontSize: '10.5px',
          fontWeight: 700,
          background: '#f3f4f6',
          color: '#4b5563',
          border: '1px solid #e5e7eb',
          marginTop: '3px',
          cursor: onClick ? 'pointer' : 'default',
        }}
        title="New Customer: No prior courier records found on Steadfast network"
      >
        <span>🆕 New (0)</span>
      </div>
    );
  }

  if (status === 'rate_limited') {
    return (
      <div
        onClick={onClick}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '3px',
          padding: '1px 6px',
          borderRadius: '9999px',
          fontSize: '10.5px',
          fontWeight: 600,
          background: '#fffbeb',
          color: '#b45309',
          border: '1px solid #fde68a',
          marginTop: '3px',
          cursor: onClick ? 'pointer' : 'default',
        }}
        title="Courier check limit reached. Retry in a few minutes."
      >
        <span>⏳ Rate limited</span>
      </div>
    );
  }

  if (status !== 'ok') {
    return null;
  }

  // Trusted: delivery rate >= 80% and >= 2 orders
  if (risk_level === 'trusted' || (total >= 2 && delivery_rate >= 80)) {
    return (
      <div
        onClick={onClick}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          padding: '1px 7px',
          borderRadius: '9999px',
          fontSize: '10.5px',
          fontWeight: 700,
          background: '#dcfce7',
          color: '#15803d',
          border: '1px solid #86efac',
          marginTop: '3px',
          cursor: onClick ? 'pointer' : 'default',
          boxShadow: '0 1px 2px rgba(22, 163, 74, 0.05)',
        }}
        title={`Steadfast Verified: ${delivered}/${total} Delivered (${delivery_rate}%) | ${cancelled} Returned`}
      >
        <span>✓ {delivery_rate}%</span>
        <span style={{ opacity: 0.85, fontSize: '9.5px', fontWeight: 600 }}>({total >= 10 ? '10+' : total})</span>
      </div>
    );
  }

  // High Risk: return rate > 40% or delivery rate < 50%
  if (risk_level === 'high_risk' || (total >= 2 && (return_rate >= 40 || delivery_rate < 50))) {
    return (
      <div
        onClick={onClick}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          padding: '1px 7px',
          borderRadius: '9999px',
          fontSize: '10.5px',
          fontWeight: 700,
          background: '#fee2e2',
          color: '#dc2626',
          border: '1px solid #fca5a5',
          marginTop: '3px',
          cursor: onClick ? 'pointer' : 'default',
          boxShadow: '0 1px 2px rgba(220, 38, 38, 0.08)',
        }}
        title={`HIGH RISK: Return Rate ${return_rate}% (${cancelled}/${total} Returned) | ${delivered} Delivered`}
      >
        <span>⚠️ {delivery_rate}%</span>
        <span style={{ fontSize: '9.5px', fontWeight: 700, color: '#991b1b' }}>Ret: {return_rate}% ({total})</span>
      </div>
    );
  }

  // Moderate
  return (
    <div
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '1px 7px',
        borderRadius: '9999px',
        fontSize: '10.5px',
        fontWeight: 700,
        background: '#eff6ff',
        color: '#2563eb',
        border: '1px solid #bfdbfe',
        marginTop: '3px',
        cursor: onClick ? 'pointer' : 'default',
      }}
      title={`Courier record: ${delivered}/${total} Delivered (${delivery_rate}%) | ${cancelled} Returned`}
    >
      <span>📊 {delivery_rate}%</span>
      <span style={{ opacity: 0.85, fontSize: '9.5px', fontWeight: 600 }}>({total})</span>
    </div>
  );
};
